import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { useAuth, api } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { User, Save, Loader2, Check, BadgeCheck, Lock, MessageSquare, BookOpen, Users } from 'lucide-react';
import { toast } from 'sonner';

const TeacherProfilePage = () => {
  const { user, updateUser } = useAuth();

  const [name, setName] = useState(user?.name || '');
  const [teacherMessage, setTeacherMessage] = useState(user?.teacher_message || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingMessage, setSavingMessage] = useState(false);
  const [messageSaved, setMessageSaved] = useState(false);
  
  // Assigned classes state
  const [assignedClasses, setAssignedClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  useEffect(() => {
    fetchAssignedClasses();
    // Update teacher message from user context
    if (user?.teacher_message) {
      setTeacherMessage(user.teacher_message);
    }
  }, [user]);

  const fetchAssignedClasses = async () => {
    try {
      const response = await api.get('/classes');
      setAssignedClasses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch assigned classes:', error);
    } finally {
      setLoadingClasses(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setSaving(true);
    try {
      const response = await api.put('/auth/profile', { name: name.trim() });
      
      if (updateUser) {
        updateUser(response.data);
      }
      
      setSaved(true);
      toast.success('Profile updated successfully!');
      
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMessage = async () => {
    setSavingMessage(true);
    try {
      const response = await api.put('/auth/profile', { teacher_message: teacherMessage.trim() });
      
      if (updateUser) {
        updateUser(response.data);
      }
      
      setMessageSaved(true);
      toast.success('Message to students updated successfully!');
      
      setTimeout(() => setMessageSaved(false), 3000);
    } catch (error) {
      toast.error('Failed to update message');
    } finally {
      setSavingMessage(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setChangingPassword(true);
    try {
      await api.put('/auth/profile', {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      setPasswordChanged(true);
      toast.success('Password changed successfully!');
      
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setTimeout(() => setPasswordChanged(false), 3000);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail === 'Current password is incorrect') {
        toast.error('Current password is incorrect');
      } else {
        toast.error('Failed to change password');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            My Profile
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Manage your profile and message to students
          </p>
        </div>

        {/* Message to Students Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-emerald-500" />
              Message to Students
            </CardTitle>
            <CardDescription>
              This message will be displayed to all students in your assigned classes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="teacher-message">
                Your Message
              </Label>
              <Textarea
                id="teacher-message"
                value={teacherMessage}
                onChange={(e) => setTeacherMessage(e.target.value)}
                placeholder="Write a motivational message or important announcement for your students..."
                rows={4}
                className="resize-none"
                data-testid="teacher-message-input"
              />
              <p className="text-sm text-slate-500">
                Tip: Use this to share announcements, homework reminders, or motivational quotes!
              </p>
            </div>

            <Button
              onClick={handleSaveMessage}
              disabled={savingMessage}
              className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
              data-testid="save-message-btn"
            >
              {savingMessage ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : messageSaved ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Saved!
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Message
                </>
              )}
            </Button>

            {teacherMessage && (
              <div className="mt-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-1">Preview:</p>
                <p className="text-sm italic text-emerald-600 dark:text-emerald-400">"{teacherMessage}"</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assigned Classes Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-indigo-500" />
              My Assigned Classes
            </CardTitle>
            <CardDescription>
              Classes you are currently teaching
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingClasses ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
              </div>
            ) : assignedClasses.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <BookOpen className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No classes assigned to you yet</p>
                <p className="text-sm">Contact your administrator to assign classes</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {assignedClasses.map((cls) => (
                  <div 
                    key={cls.id}
                    className="p-4 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-center"
                  >
                    <BookOpen className="h-6 w-6 mx-auto mb-2 text-indigo-500" />
                    <p className="font-semibold text-indigo-700 dark:text-indigo-300">{cls.name}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Teacher Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-cyan-500" />
              Teacher Information
            </CardTitle>
            <CardDescription>
              Your account details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-white/5">
                <p className="text-xs uppercase tracking-wider mb-1 text-slate-400">
                  Email
                </p>
                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                  {user?.email || '-'}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-white/5">
                <p className="text-xs uppercase tracking-wider mb-1 text-slate-400">
                  Role
                </p>
                <p className="text-sm font-medium capitalize text-indigo-600 dark:text-indigo-400">
                  {user?.role || '-'}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-white/5 col-span-2">
                <p className="text-xs uppercase tracking-wider mb-1 text-slate-400">
                  Assigned Classes
                </p>
                <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400">
                  {assignedClasses.length} Class{assignedClasses.length !== 1 ? 'es' : ''}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-violet-500" />
              Edit Profile
            </CardTitle>
            <CardDescription>
              Update your display name
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Your Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                data-testid="profile-name-input"
              />
            </div>

            <Button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="w-full sm:w-auto bg-gradient-to-r from-violet-500 to-purple-500 text-white"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : saved ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Saved!
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-500" />
              Change Password
            </CardTitle>
            <CardDescription>
              Update your login password
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">
                Current Password
              </Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                data-testid="current-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password">
                New Password
              </Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                data-testid="new-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">
                Confirm New Password
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              onClick={handleChangePassword}
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-orange-500 text-white"
              data-testid="change-password-btn"
            >
              {changingPassword ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Changing...
                </>
              ) : passwordChanged ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Changed!
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Change Password
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TeacherProfilePage;
